import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext, TasksContext } from '@/App';
import { Link } from 'react-router-dom';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Calendar, ListChecks, CheckCircle, Clock, AlertTriangle, Sparkles,
  ArrowRight, Shield, Activity, Brain, BookOpen, Plus, Zap, ChevronRight
} from 'lucide-react';
import ClashReviewPanel from '@/components/ClashReviewPanel';
import SuggestionsPanel from '@/components/SuggestionsPanel';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import SubscriptionBanner from '@/components/SubscriptionBanner';

const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(BigCalendar);
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Greeting based on time of day ──
const getGreeting = (name) => {
  const h = new Date().getHours();
  const first = name?.split(' ')[0] || '';
  if (h < 12) return `Good morning, ${first} ☀️`;
  if (h < 17) return `Good afternoon, ${first}`;
  return `Good evening, ${first} 🌙`;
};

// ── Today's focus card — what to do RIGHT NOW ──
const TodayFocus = ({ tasks, onNavigate }) => {
  const now = new Date();
  const todayTasks = tasks.filter(t => {
    if (!t.scheduled_time) return false;
    return new Date(t.scheduled_time).toDateString() === now.toDateString() && t.status === 'scheduled';
  }).sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time));

  const nextTask = todayTasks.find(t => new Date(t.scheduled_time) > now);
  const currentTask = todayTasks.find(t => {
    const start = new Date(t.scheduled_time);
    const end = new Date(start.getTime() + (t.duration || 60) * 60000);
    return start <= now && now <= end;
  });

  const pending = tasks.filter(t => t.status === 'pending').length;

  if (currentTask) {
    return (
      <div className="glass-card p-5 mb-5 relative overflow-hidden"
        style={{ border: '1px solid rgba(0,217,255,0.3)', background: 'rgba(0,217,255,0.05)' }}>
        <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10"
          style={{ background: '#00D9FF', filter: 'blur(40px)', transform: 'translate(30%,-30%)' }}/>
        <div className="flex items-start gap-4 relative">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(0,217,255,0.15)', border: '1px solid rgba(0,217,255,0.3)' }}>
            <Zap size={18} className="text-cyan-400"/>
          </div>
          <div className="flex-1">
            <p className="text-xs text-cyan-400 font-semibold uppercase tracking-wider mb-1">Happening now</p>
            <p className="text-white font-semibold">{currentTask.title}</p>
            <p className="text-sm text-gray-400 mt-0.5">{currentTask.duration} min · {currentTask.priority} priority</p>
          </div>
        </div>
      </div>
    );
  }

  if (nextTask) {
    const minsUntil = Math.round((new Date(nextTask.scheduled_time) - now) / 60000);
    const label = minsUntil < 60 ? `in ${minsUntil} min` : `at ${new Date(nextTask.scheduled_time).toLocaleTimeString('en',{hour:'2-digit',minute:'2-digit'})}`;
    return (
      <div className="glass-card p-5 mb-5 relative overflow-hidden"
        style={{ border: '1px solid rgba(167,139,250,0.2)', background: 'rgba(167,139,250,0.04)' }}>
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.25)' }}>
            <Clock size={18} className="text-purple-400"/>
          </div>
          <div className="flex-1">
            <p className="text-xs text-purple-400 font-semibold uppercase tracking-wider mb-1">Up next — {label}</p>
            <p className="text-white font-semibold">{nextTask.title}</p>
            <p className="text-sm text-gray-400 mt-0.5">{nextTask.duration} min block</p>
          </div>
        </div>
      </div>
    );
  }

  if (pending > 0) {
    return (
      <Link to="/tasks">
        <div className="glass-card p-5 mb-5 hover:bg-white/6 transition-colors cursor-pointer group"
          style={{ border: '1px solid rgba(234,179,8,0.2)', background: 'rgba(234,179,8,0.04)' }}>
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.25)' }}>
              <ListChecks size={18} className="text-yellow-400"/>
            </div>
            <div className="flex-1">
              <p className="text-xs text-yellow-400 font-semibold uppercase tracking-wider mb-1">Action needed</p>
              <p className="text-white font-semibold">{pending} task{pending !== 1 ? 's' : ''} waiting to be scheduled</p>
              <p className="text-sm text-gray-400 mt-0.5">Let AI find the best time →</p>
            </div>
            <ChevronRight size={16} className="text-gray-600 group-hover:text-gray-300 transition-colors"/>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to="/tasks">
      <div className="glass-card p-5 mb-5 hover:bg-white/6 transition-colors cursor-pointer group"
        style={{ border: '1px solid rgba(34,197,94,0.2)', background: 'rgba(34,197,94,0.04)' }}>
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.25)' }}>
            <CheckCircle size={18} className="text-green-400"/>
          </div>
          <div className="flex-1">
            <p className="text-xs text-green-400 font-semibold uppercase tracking-wider mb-1">All clear</p>
            <p className="text-white font-semibold">Nothing scheduled right now</p>
            <p className="text-sm text-gray-400 mt-0.5">Add a new task to get started →</p>
          </div>
          <ChevronRight size={16} className="text-gray-600 group-hover:text-gray-300 transition-colors"/>
        </div>
      </div>
    </Link>
  );
};

// ── Contextual AI nudge bar — changes based on user state ──
const SpaceBar = ({ tasks, userName }) => {
  const pending = tasks.filter(t => t.status === 'pending').length;
  const todayStr = new Date().toDateString();
  const scheduled = tasks.filter(t => t.status === 'scheduled' && t.scheduled_time && new Date(t.scheduled_time).toDateString() === todayStr);
  const totalMins = scheduled.reduce((s, t) => s + (t.duration || 0), 0);
  const completed = tasks.filter(t => t.status === 'completed').length;
  const overloaded = totalMins > 480;

  const nudges = [];

  if (overloaded) nudges.push({
    icon: AlertTriangle, color: '#f97316',
    text: `You've scheduled ${Math.round(totalMins/60)}h today — that's a lot. Consider moving something flexible.`,
    link: '/health', cta: 'Check your load'
  });

  if (pending > 3) nudges.push({
    icon: Sparkles, color: '#a78bfa',
    text: `${pending} tasks are waiting for a time slot. AI can schedule them in seconds.`,
    link: '/tasks', cta: 'Schedule now'
  });

  if (completed > 0 && completed % 5 === 0) nudges.push({
    icon: BookOpen, color: '#22c55e',
    text: `You've completed ${completed} tasks. A quick reflection could help you understand what's working.`,
    link: '/reflection', cta: 'Reflect'
  });

  if (!nudges.length) nudges.push({
    icon: Shield, color: '#00D9FF',
    text: 'Your schedule looks balanced. Protect your breathing space and stay intentional.',
    link: '/non-negotiables', cta: 'Protect time'
  });

  const nudge = nudges[0];
  const Icon = nudge.icon;

  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-2xl mb-6"
      style={{ background: `${nudge.color}08`, border: `1px solid ${nudge.color}20` }}>
      <Icon size={15} style={{ color: nudge.color, flexShrink: 0 }}/>
      <p className="text-sm text-gray-300 flex-1">{nudge.text}</p>
      <Link to={nudge.link}
        className="text-xs font-semibold flex items-center gap-1 flex-shrink-0 hover:opacity-80 transition-opacity"
        style={{ color: nudge.color }}>
        {nudge.cta} <ArrowRight size={11}/>
      </Link>
    </div>
  );
};

// ── Quick action row ──
const QuickActions = ({ pendingCount }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
    {[
      { to: '/tasks',          icon: Plus,      label: 'Add Task',      color: '#00D9FF' },
      { to: '/health',         icon: Activity,  label: 'Health Check',  color: '#a78bfa' },
      { to: '/reflection',     icon: BookOpen,  label: 'This Week',     color: '#22c55e' },
      { to: '/life-report',    icon: Brain,     label: 'Life Report',   color: '#f59e0b' },
    ].map(({ to, icon: Icon, label, color }) => (
      <Link key={to} to={to}
        className="glass-card p-4 flex flex-col items-center gap-2 text-center hover:bg-white/8 transition-all hover:scale-[1.02] active:scale-[0.98]">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ background: `${color}12`, border: `1px solid ${color}22` }}>
          <Icon size={17} style={{ color }}/>
        </div>
        <span className="text-xs font-medium text-gray-300">{label}</span>
      </Link>
    ))}
  </div>
);

// ── Mini stat strip ──
const StatStrip = ({ tasks }) => {
  const scheduled = tasks.filter(t => t.status === 'scheduled').length;
  const completed = tasks.filter(t => t.status === 'completed').length;
  const pending   = tasks.filter(t => t.status === 'pending').length;
  const totalMins = tasks.filter(t => t.status === 'scheduled').reduce((s, t) => s + (t.duration || 0), 0);

  return (
    <div className="grid grid-cols-4 gap-3 mb-6">
      {[
        { label: 'Scheduled', value: scheduled, color: '#00D9FF' },
        { label: 'Hours',     value: `${Math.round(totalMins/60 * 10)/10}h`, color: '#a78bfa', raw: true },
        { label: 'Pending',   value: pending,   color: '#eab308' },
        { label: 'Done',      value: completed, color: '#22c55e' },
      ].map(({ label, value, color }) => (
        <div key={label} className="glass-card p-3 text-center">
          <p className="text-xl font-bold" style={{ color }}>{value}</p>
          <p className="text-xs text-gray-500 mt-0.5">{label}</p>
        </div>
      ))}
    </div>
  );
};

// ── Main Dashboard ──
const Dashboard = () => {
  const { auth } = useContext(AuthContext);
  const { tasksVersion } = useContext(TasksContext);
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [nonNegotiables, setNonNegotiables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const didInitialNav = React.useRef(false);
  const [trips, setTrips] = useState([]);
  const [clashes, setClashes] = useState([]);
  const [showClashes, setShowClashes] = useState(false);
  const [glowIds, setGlowIds] = useState([]);

  const loadTasks = useCallback(async () => {
    try {
      const [res, nnRes] = await Promise.all([
        axios.get(`${API}/tasks`, { headers: { Authorization: `Bearer ${auth.token}` } }),
        axios.get(`${API}/non-negotiables`, { headers: { Authorization: `Bearer ${auth.token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/trips`, { headers: { Authorization: `Bearer ${auth.token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/clashes?tz_offset_minutes=${-new Date().getTimezoneOffset()}`, { headers: { Authorization: `Bearer ${auth.token}` } }).catch(() => ({ data: [] })),
      ]).then(([a, b, c, d]) => { setTrips(c.data || []); setClashes(d.data || []); return [a, b]; });
      setTasks(res.data);
      setNonNegotiables(nnRes.data || []);
      const evts = res.data
        .filter(t => t.scheduled_time)
        .map(t => ({
          id: t.id,
          title: t.title,
          start: new Date(t.scheduled_time),
          end: new Date(new Date(t.scheduled_time).getTime() + (t.duration || 60) * 60000),
          resource: t,
        }));
      setEvents(evts);

      // Auto-navigate the calendar to the next upcoming scheduled task so
      // newly-created tasks are visible without the user having to scroll or
      // click "Next". Falls back to today if nothing upcoming.
      const now = new Date();
      const upcoming = evts
        .filter(e => e.start >= now)
        .sort((a, b) => a.start - b.start)[0];
      const target = upcoming?.start || evts.sort((a, b) => b.start - a.start)[0]?.start;
      if (target && !didInitialNav.current) { setCurrentDate(target); didInitialNav.current = true; }
    } catch {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [auth.token]);

  useEffect(() => { loadTasks(); }, [tasksVersion, loadTasks]);

  const handleDeleteTask = async (task) => {
    try {
      await axios.delete(`${API}/tasks/${task.id}`, { headers: { Authorization: `Bearer ${auth.token}` } });
      toast.success(`"${task.title}" deleted`);
      loadTasks();
    } catch {
      toast.error('Failed to delete task');
    }
  };

  const handleToggleDone = async (t) => {
    const next = t.status === 'completed' ? 'scheduled' : 'completed';
    try {
      await axios.put(`${API}/tasks/${t.id}`, { status: next }, { headers: { Authorization: `Bearer ${auth.token}` } });
      toast.success(next === 'completed' ? `Nice work — \"${t.title}\" is done ✅` : `\"${t.title}\" moved back to scheduled`);
      loadTasks();
    } catch {
      toast.error('Could not update task');
    }
  };

  const cancelTrip = async (tr) => {
    try {
      const res = await axios.delete(`${API}/trips/${tr.id}`, { headers: { Authorization: `Bearer ${auth.token}` } });
      toast.success(`Trip cancelled — ${res.data.restored_tasks} paused task(s) restored`);
      loadTasks();
    } catch {
      toast.error('Could not cancel trip');
    }
  };

  const isDraggable = (event) => !event.isNonNegotiable && !event.isTrip && event.resource?.status === 'scheduled';

  const moveTask = async ({ event, start, end }) => {
    if (!isDraggable(event)) return;
    const prev = events;
    const duration = Math.max(15, Math.round((end - start) / 60000));
    setEvents(evts => evts.map(e => (e.id === event.id ? { ...e, start, end, resource: { ...e.resource, scheduled_time: start.toISOString(), duration } } : e)));
    try {
      const res = await axios.put(`${API}/tasks/${event.id}`, {
        scheduled_time: start.toISOString(), duration, source: 'drag', tz_offset_minutes: -new Date().getTimezoneOffset(),
      }, { headers: { Authorization: `Bearer ${auth.token}` } });
      const soft = res.data.conflicts || [];
      toast.success(`Moved "${event.title}" to ${moment(start).format('ddd h:mm A')}`, {
        description: res.data.learned || undefined, duration: 5000,
      });
      if (soft.length) toast.warning(`Overlaps ${soft.map(c => c.label).join(', ')}`);
      loadTasks();
    } catch (err) {
      setEvents(prev);
      const msg = err.response?.data?.detail?.message || 'Could not move task';
      toast.error(msg);
    }
  };

  const handleEventClick = (event) => {
    if (event.isTrip) {
      const tr = event.resource;
      toast(`${event.title} · ${tr.start_date} → ${tr.end_date}. Normal scheduling is blocked these days.`, {
        duration: 7000, action: { label: 'Cancel trip', onClick: () => cancelTrip(tr) },
      });
      return;
    }
    if (event.isNonNegotiable) {
      toast.info(`${event.title} is a protected block. Manage it on the Non-Negotiables page.`, { duration: 4000 });
      return;
    }
    if (event.clash) {
      toast.warning(`"${event.resource.title}" clashes with ${event.clash.clashes_with.join(', ')} (${event.clash.local})`, {
        duration: 8000, action: { label: 'Move with AI', onClick: () => moveOneWithAI(event.clash) },
      });
      return;
    }
    const t = event.resource;
    toast(`${t.title} · ${t.duration} min · ${t.priority} priority${t.status === 'completed' ? ' · done' : ''}${t.status === 'paused' ? ' · paused for trip' : ''}`, {
      duration: 7000,
      description: t.ai_reasons?.length ? `Why here: ${t.ai_reasons.join(' · ')}` : undefined,
      action: {
        label: t.status === 'completed' ? 'Undo done' : 'Mark done',
        onClick: () => handleToggleDone(t),
      },
      cancel: {
        label: 'Delete',
        onClick: () => handleDeleteTask(t),
      },
    });
  };

  // Project recurring non-negotiables (e.g. Sleep on Mon-Sun 23:00–07:00) into
  // concrete calendar events around the currently viewed date.
  const nnEvents = React.useMemo(() => {
    if (!nonNegotiables.length) return [];
    const dayMap = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
    const rangeStart = moment(currentDate).startOf('month').subtract(7, 'days');
    const out = [];
    for (const nn of nonNegotiables) {
      const raw = (nn.days || []).map(d => (d || '').toLowerCase());
      const days = raw.some(d => d.startsWith('every'))
        ? new Set([0, 1, 2, 3, 4, 5, 6])
        : new Set(raw.map(d => dayMap[d.slice(0, 3)]).filter(d => d !== undefined));
      if (!days.size) continue;
      const [sh, sm] = (nn.start_time || '0:0').split(':').map(Number);
      const [eh, em] = (nn.end_time || '0:0').split(':').map(Number);
      for (let i = 0; i < 45; i++) {
        const day = moment(rangeStart).add(i, 'days');
        if (!days.has(day.day())) continue;
        const start = day.clone().hour(sh || 0).minute(sm || 0).second(0).toDate();
        const end = day.clone().hour(eh || 0).minute(em || 0).second(0).toDate();
        const pausedByTrip = trips.some(tr => (tr.paused_nn_ids || []).includes(nn.id)
          && day.format('YYYY-MM-DD') >= tr.start_date && day.format('YYYY-MM-DD') <= tr.end_date);
        if (pausedByTrip) continue;
        const base = { title: `🛡 ${nn.label}`, isNonNegotiable: true, nnColor: nn.color || '#a78bfa', resource: nn };
        if (end <= start) {
          // Overnight window (e.g. 22:30 → 06:00): render ONE block per night from start to
          // midnight, labelled with the real end time, instead of two separate blocks.
          out.push({ ...base, title: `🛡 ${nn.label} · until ${nn.end_time}`, id: `nn-${nn.id}-${i}`, start, end: day.clone().endOf('day').toDate() });
        } else {
          out.push({ ...base, id: `nn-${nn.id}-${i}`, start, end });
        }
      }
    }
    return out;
  }, [nonNegotiables, currentDate, trips]);

  const tripEvents = React.useMemo(() => trips.map(tr => (tr.kind === 'event' && tr.start_time ? {
    id: `trip-${tr.id}`, title: `★ ${tr.title || 'Event'}`, isTrip: true,
    start: moment(`${tr.start_date} ${tr.start_time}`).toDate(), end: moment(`${tr.end_date} ${tr.end_time}`).toDate(), resource: tr,
  } : {
    id: `trip-${tr.id}`, title: tr.kind === 'event' ? `★ ${tr.title || 'Event'}` : `✈ ${tr.destination ? `Trip: ${tr.destination}` : 'Trip'}`, allDay: true, isTrip: true,
    start: moment(tr.start_date).startOf('day').toDate(), end: moment(tr.end_date).add(1, 'day').startOf('day').toDate(), resource: tr,
  })), [trips]);

  const clashMap = React.useMemo(() => Object.fromEntries(clashes.map(c => [c.task_id, c])), [clashes]);
  const allEvents = React.useMemo(() => [
    ...events.map(e => (clashMap[e.id] ? { ...e, clash: clashMap[e.id], title: `⚠ ${e.title} · Clashes with ${clashMap[e.id].clashes_with.join(', ')}` } : e)),
    ...nnEvents, ...tripEvents,
  ], [events, nnEvents, tripEvents, clashMap]);

  const afterClashMove = (results) => {
    const ids = results.filter(r => r.action === 'moved').map(r => r.task_id);
    setGlowIds(ids);
    setTimeout(() => setGlowIds([]), 4000);
    const first = results.find(r => r.new_time);
    if (first) setCurrentDate(new Date(first.new_time));
    loadTasks();
  };

  const moveOneWithAI = async (c) => {
    try {
      const res = await axios.post(`${API}/clashes/resolve`, { task_ids: [c.task_id], tz_offset_minutes: -new Date().getTimezoneOffset() },
        { headers: { Authorization: `Bearer ${auth.token}` } });
      const r = res.data.results?.[0];
      if (r?.action === 'moved') toast.success(`Moved "${r.title}" to ${r.local}`, { description: r.reasons.join(' · ') });
      else if (r) toast.warning(`"${r.title}" moved to Pending — no free slot within 14 days`);
      afterClashMove(res.data.results || []);
    } catch {
      toast.error('Could not move the task');
    }
  };

  const eventStyleGetter = (event) => {
    const r = baseEventStyle(event);
    if (event.clash) r.style = { ...r.style, outline: '2px solid #ef4444', outlineOffset: '-1px', boxShadow: '0 0 0 3px rgba(239,68,68,0.25)' };
    if (glowIds.includes(event.id)) r.style = { ...r.style, boxShadow: '0 0 14px 3px rgba(0,217,255,0.75)', transition: 'box-shadow 0.6s ease' };
    return r;
  };

  const baseEventStyle = (event) => {
    if (event.isTrip) {
      return { style: { background: 'rgba(251,191,36,0.18)', border: '1px solid rgba(251,191,36,0.6)', borderRadius: 8,
        color: '#fcd34d', fontWeight: 600, fontSize: 12, padding: '2px 6px' }};
    }
    if (event.resource?.status === 'paused') {
      return { style: { background: 'rgba(148,163,184,0.12)', border: '1px dashed rgba(148,163,184,0.5)', borderRadius: 8,
        color: '#94a3b8', fontWeight: 500, fontSize: 12, padding: '2px 6px', opacity: 0.7 }};
    }
    if (event.isNonNegotiable) {
      const c = event.nnColor;
      return { style: {
        background: `repeating-linear-gradient(135deg, ${c}26, ${c}26 6px, ${c}14 6px, ${c}14 12px)`,
        border: `1px dashed ${c}`, borderRadius: 8, color: c,
        fontWeight: 600, fontSize: 12, padding: '2px 6px',
      }};
    }
    const t = event.resource;
    if (t?.status === 'completed') {
      return { style: { background: 'rgba(34,197,94,0.16)', border: '1px solid rgba(34,197,94,0.5)', borderRadius: 8,
        color: '#86efac', textDecoration: 'line-through', fontWeight: 600, fontSize: 12, padding: '2px 6px' }};
    }
    const colors = {
      critical: { bg: 'linear-gradient(135deg,#ef4444,#dc2626)', border: '#ef4444' },
      high:     { bg: 'linear-gradient(135deg,#f97316,#ea580c)', border: '#f97316' },
      medium:   { bg: 'linear-gradient(135deg,#00D9FF,#0EA5E9)', border: '#00D9FF' },
      low:      { bg: 'linear-gradient(135deg,#22c55e,#16a34a)', border: '#22c55e'  },
    };
    const c = colors[t?.priority] || colors.medium;
    return { style: { background: c.bg, border: `1px solid ${c.border}40`, borderRadius: 8, color: '#fff', fontWeight: 600, fontSize: 12, padding: '2px 6px' }};
  };

  // Calendar should open already-scrolled to the user's next event (or "an
  // hour before now" as a fallback) so newly-added tasks don't get buried
  // below the visible window.
  const scrollToTime = (() => {
    const now = new Date();
    const upcoming = events
      .map(e => e.start)
      .filter(s => s >= now)
      .sort((a, b) => a - b)[0];
    const base = upcoming ? new Date(upcoming) : now;
    // Back up 1 hour so the event sits a little below the top of the viewport.
    base.setHours(Math.max(6, base.getHours() - 1), 0, 0, 0);
    return base;
  })();

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center gradient-bg">
      <div className="text-center">
        <div className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin mx-auto mb-3"
          style={{ borderColor: '#00D9FF', borderTopColor: 'transparent' }}/>
        <p className="text-gray-500 text-sm">Loading your space…</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen gradient-bg">
      <SubscriptionBanner />
      <div className="max-w-6xl mx-auto px-6 py-8">

        {/* Greeting */}
        <div className="mb-6">
          <h1 className="text-2xl heading-font font-bold text-white mb-1">
            {getGreeting(auth.user?.name)}
          </h1>
          <p className="text-sm text-gray-500">
            {new Date().toLocaleDateString('en', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {clashes.length > 0 && (
          <button onClick={() => setShowClashes(true)} data-testid="clash-banner"
            className="w-full mb-4 flex items-center justify-between px-4 py-2.5 rounded-xl text-sm text-amber-200 transition-colors hover:bg-amber-400/10"
            style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.25)' }}>
            <span>⚠ {clashes.length} task{clashes.length !== 1 ? 's' : ''} clash with your protected time</span>
            <span className="font-semibold text-amber-300">Review →</span>
          </button>
        )}
        <Dialog open={showClashes} onOpenChange={setShowClashes}>
          <DialogContent className="bg-[#0A0E27] border-white/10 p-0 max-w-lg" data-testid="clash-dialog">
            <DialogTitle className="sr-only">Clash review</DialogTitle>
            {showClashes && (
              <ClashReviewPanel clashes={clashes} onKeep={() => setShowClashes(false)}
                onDone={afterClashMove} />
            )}
          </DialogContent>
        </Dialog>

        <SuggestionsPanel />

        {/* Today's focus card */}
        <TodayFocus tasks={tasks} />

        {/* Contextual AI nudge */}
        <SpaceBar tasks={tasks} userName={auth.user?.name}/>

        {/* Stat strip */}
        <StatStrip tasks={tasks}/>

        {/* Quick actions */}
        <QuickActions pendingCount={tasks.filter(t => t.status === 'pending').length}/>

        {/* Calendar */}
        <div className="dark-card p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg heading-font font-bold text-white">Your Schedule</h2>
            <div className="flex items-center gap-1 p-1 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              {['day','week','month'].map(v => (
                <button key={v} data-testid={`calendar-view-${v}-btn`} onClick={() => setView(v)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-all ${
                    view === v ? 'text-cyan-400 bg-cyan-400/12' : 'text-gray-500 hover:text-gray-300'
                  }`}>
                  {v}
                </button>
              ))}
            </div>
          </div>

          <p className="text-[11px] text-gray-500 mb-2" data-testid="drag-hint">Drag a task to move it, or stretch its edge to change the length — SpaceCal learns from every adjustment.</p>
          <div style={{ height: 720 }} className="calendar-dark-theme" data-testid="dashboard-calendar">
            <DnDCalendar
              localizer={localizer}
              draggableAccessor={isDraggable}
              resizableAccessor={isDraggable}
              onEventDrop={moveTask}
              onEventResize={moveTask}
              resizable
              events={allEvents}
              startAccessor="start"
              endAccessor="end"
              view={view}
              onView={setView}
              views={['day','week','month']}
              style={{ height: '100%' }}
              date={currentDate}
              onNavigate={setCurrentDate}
              onSelectEvent={handleEventClick}
              eventPropGetter={eventStyleGetter}
              scrollToTime={scrollToTime}
              min={new Date(1970, 0, 1, 6, 0, 0)}
              max={new Date(1970, 0, 1, 23, 59, 59)}
              step={30}
              timeslots={2}
            />
          </div>

          {/* Calendar legend */}
          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/5">
            {[
              { label: 'Critical', color: '#ef4444' },
              { label: 'High',     color: '#f97316' },
              { label: 'Medium',   color: '#00D9FF' },
              { label: 'Low',      color: '#22c55e' },
              { label: 'Protected', color: '#a78bfa' },
            ].map(({ label, color }) => (
              <span key={label} className="flex items-center gap-1.5 text-xs text-gray-500">
                <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: color }}/>
                {label}
              </span>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Dashboard;
